// Récolte : relire un CV édité à l'écran et le rendre au JSON.
//
// Le pendant exact du marquage `data-field` posé par `renderResumeToHTML` en
// mode éditable (resumeHtml.ts). Sans ce module, la preview éditable était un
// trompe-l'œil : on tapait dedans, ça s'affichait, et tous les exports
// repartaient du JSON stocké — la modification n'existait nulle part.
//
// Principes :
//  - on part TOUJOURS du profil d'origine et on n'écrase que ce qui est
//    marqué. Un champ absent du DOM (section masquée par les réglages, rendu
//    partiel) garde sa valeur : masquer une section ne doit pas l'effacer ;
//  - un conteneur `data-item` disparu du DOM = entrée supprimée. C'est le seul
//    moyen de rendre compte d'une expérience effacée à la main ;
//  - aucune dépendance DOM concrète : on prend un `ParentNode` (document
//    d'iframe, élément, ou implémentation de test). Le paquet reste sans
//    dépendance plateforme.

import type { ResumeProfile, WorkItem, EducationItem, ProjectItem, LanguageItem } from './schema'

/** Sous-ensemble du DOM dont on a besoin — évite d'imposer `lib.dom`. */
interface HarvestNode {
  getAttribute(name: string): string | null
  textContent: string | null
  querySelectorAll(selectors: string): ArrayLike<HarvestNode>
}
interface HarvestRoot {
  querySelectorAll(selectors: string): ArrayLike<HarvestNode>
}

/**
 * Normalise le texte d'une zone éditable.
 *
 * Le contenteditable produit des sauts de ligne, des `<br>`, des espaces
 * insécables et des blancs de fin dès qu'on tape ou qu'on colle : sans ce
 * passage, un simple aller-retour dans le champ ferait diverger le JSON du
 * texte visible et compterait comme une modification.
 */
function cleanText(raw: string | null): string {
  return (raw ?? '')
    .replace(/ /g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Découpe une liste séparée par des virgules (compétences). */
function splitComma(raw: string | null): string[] {
  return cleanText(raw).split(',').map((s) => s.trim()).filter(Boolean)
}

/**
 * Relit « Français (Langue maternelle), Anglais (C1) ».
 * Une entrée sans parenthèses reste une langue sans niveau — c'est déjà ce que
 * le rendu produit dans ce cas, l'aller-retour est donc stable.
 */
function parseLanguages(raw: string | null): LanguageItem[] {
  return splitComma(raw).map((entry) => {
    const m = entry.match(/^(.*?)\s*\((.*)\)$/)
    return m
      ? { language: m[1].trim(), fluency: m[2].trim() }
      : { language: entry, fluency: '' }
  })
}

/** Clone profond sans dépendre de `structuredClone` (Node 18 / jsdom anciens). */
function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T
}

/**
 * Reconstruit un profil à partir du document édité.
 *
 * @param root  le document (ou l'élément racine) contenant le CV rendu éditable
 * @param base  le profil qui a servi à produire ce rendu
 */
export function harvestResume(root: HarvestRoot, base: ResumeProfile): ResumeProfile {
  const out = clone(base)

  // 1. Les champs simples et les listes, par chemin marqué.
  const marked = root.querySelectorAll('[data-field]')
  for (let i = 0; i < marked.length; i++) {
    const el = marked[i]
    const path = el.getAttribute('data-field')
    if (!path) continue
    const kind = el.getAttribute('data-list')

    if (kind === 'lines') {
      // Puces : l'ordre du DOM fait foi (ajout, suppression, déplacement).
      const lis = el.querySelectorAll('li')
      const lines: string[] = []
      for (let j = 0; j < lis.length; j++) {
        const line = cleanText(lis[j].textContent)
        if (line) lines.push(line)
      }
      setPath(out, path, lines)
    } else if (kind === 'comma') {
      setPath(out, path, splitComma(el.textContent))
    } else if (kind === 'langs') {
      setPath(out, path, parseLanguages(el.textContent))
    } else {
      setPath(out, path, cleanText(el.textContent))
    }
  }

  // 2. Les entrées entièrement supprimées à l'écran.
  //
  // Fait APRÈS l'écriture des champs, et par filtrage de l'ordre d'origine :
  // l'affichage des expériences est anté-chronologique alors que le JSON garde
  // son ordre de saisie. Reprendre l'ordre du DOM réordonnerait le profil sans
  // que personne ne l'ait demandé (et le rendu re-trierait de toute façon).
  const present = new Set<string>()
  const items = root.querySelectorAll('[data-item]')
  for (let i = 0; i < items.length; i++) {
    const p = items[i].getAttribute('data-item')
    if (p) present.add(p)
  }
  // Une section qui n'a produit AUCUN conteneur n'a pas été rendue du tout
  // (masquée dans les réglages, ou vide) : on ne peut rien en conclure, donc
  // on n'y touche pas. Seule une section rendue peut témoigner d'une suppression.
  out.work = filterRendered(out.work, 'work', present)
  out.education = filterRendered(out.education, 'education', present)
  out.projects = filterRendered(out.projects, 'projects', present)

  return out
}

function filterRendered<T>(list: T[], prefix: string, present: Set<string>): T[] {
  const anyRendered = list.some((_, i) => present.has(`${prefix}.${i}`))
  if (!anyRendered) return list
  return list.filter((_, i) => present.has(`${prefix}.${i}`))
}

/**
 * Écrit une valeur au chemin donné (`basics.summary`, `work.2.highlights`).
 *
 * Strictement défensif : on ne CRÉE jamais de nœud intermédiaire ni d'entrée de
 * tableau. Un chemin qui ne correspond plus à rien dans le profil (rendu
 * fabriqué à la main, HTML d'une version précédente) est ignoré, pas inventé.
 */
function setPath(target: unknown, path: string, value: unknown): void {
  const parts = path.split('.')
  let node: any = target
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i]
    if (node == null || typeof node !== 'object') return
    if (Array.isArray(node)) {
      const idx = Number(key)
      if (!Number.isInteger(idx) || idx < 0 || idx >= node.length) return
      node = node[idx]
    } else {
      if (!(key in node)) return
      node = node[key]
    }
  }
  const last = parts[parts.length - 1]
  if (node == null || typeof node !== 'object') return
  if (Array.isArray(node)) {
    const idx = Number(last)
    if (!Number.isInteger(idx) || idx < 0 || idx >= node.length) return
    node[idx] = value
  } else {
    if (!(last in node)) return
    node[last] = value
  }
}

/** Types réexportés pour les consommateurs qui manipulent le résultat. */
export type { WorkItem, EducationItem, ProjectItem, LanguageItem }
